import usePageMeta from '../hooks/usePageMeta';
import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { sendContact } from '../api';
import { toast } from 'sonner';

export default function Contact() {
  usePageMeta('Contact Hitera | Designer Wear – Mumbai, India', 'Get in touch with Hitera — luxury designer wear brand based in Mumbai, India. Enquiries, orders and appointments.');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill all required fields'); return; }
    try {
      await sendContact(form);
      toast.success('Message sent', { description: 'Our client care team will reach out shortly.' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      toast.error('Could not send message. Please try again.');
    }
  };

  return (
    <div className="bg-white">
      <div className="relative h-[42vh] overflow-hidden bg-burgundy-950">
        <img src="https://images.unsplash.com/photo-1610047614301-13c63f00c032?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="Contact" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-burgundy-950/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-gold tracking-luxe text-xs uppercase mb-3 fade-up">We’d Love To Hear From You</p>
          <h1 className="font-display text-5xl md:text-6xl text-cream fade-up">Client Care</h1>
        </div>
      </div>

      <div className="max-w-[1300px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16">
        {/* Info */}
        <div>
          <h2 className="font-display text-4xl text-burgundy-900 mb-3">Visit The Atelier</h2>
          <div className="gold-divider !mx-0" />
          <p className="text-burgundy-900/70 mb-10 text-sm leading-relaxed max-w-md">Book a private appointment at our flagship atelier for a bespoke styling experience, or reach out to our client care team for any assistance.</p>
          <div className="space-y-7">
            {[
              { icon: MapPin, t: 'Flagship Atelier', d: 'Kandivali West, Mumbai, India' },
              { icon: Phone, t: 'Call Us', d: '+91 96536 29294' },
              { icon: Mail, t: 'Email', d: 'hitera0777@gmail.com' },
              { icon: Clock, t: 'Atelier Hours', d: 'Mon – Sat, 11:00 AM – 8:00 PM' },
            ].map((c) => (
              <div key={c.t} className="flex gap-4">
                <div className="w-11 h-11 rounded-full border border-gold flex items-center justify-center flex-shrink-0">
                  <c.icon size={19} className="text-gold-dark" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs tracking-wideluxe uppercase text-gold-dark mb-1">{c.t}</p>
                  <p className="text-burgundy-900/80 text-sm">{c.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-cream/60 p-8 md:p-10">
          <h2 className="font-display text-3xl text-burgundy-900 mb-6">Send A Message</h2>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Name *</label>
                <input value={form.name} onChange={set('name')} className="w-full bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold" />
              </div>
              <div>
                <label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Email *</label>
                <input type="email" value={form.email} onChange={set('email')} className="w-full bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold" />
              </div>
            </div>
            <div>
              <label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Subject</label>
              <input value={form.subject} onChange={set('subject')} className="w-full bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold" />
            </div>
            <div>
              <label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Message *</label>
              <textarea rows={5} value={form.message} onChange={set('message')} className="w-full bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold resize-none" />
            </div>
            <button type="submit" className="w-full bg-burgundy-900 text-cream py-4 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors">Send Message</button>
          </form>
        </div>
      </div>
    </div>
  );
}
